import React, { useState, useEffect, useMemo } from "react";
import Layout from "components/TranslationOffice/Layout";
import LanguageSelector from "components/TranslationOffice/LanguageSelector";
import TranslationEditor from "components/TranslationOffice/TranslationEditor";
import IssuesPanel from "components/TranslationOffice/IssuesPanel";
import SearchBar from "components/TranslationOffice/SearchBar";
import JsonViewer from "components/TranslationOffice/JsonViewer";
import ViewToggle from "components/TranslationOffice/ViewToggle";
import FilterBar from "components/TranslationOffice/FilterBar";
import { useTranslations } from "components/TranslationOffice/hooks/useTranslations";
import {
  TranslationFilters,
  TranslationObject,
} from "components/TranslationOffice/types";

type ViewType = "key" | "json";

const TranslationOffice: React.FC = () => {
  const {
    languages,
    translations,
    masterTranslation,
    masterLang,
    selectedLang,
    setSelectedLang,
    isLoading,
    error,
    getLanguageIssues,
    addMissingKeysToLang,
    copyKeyFromMaster,
    updateTranslation,
    translateWithAI,
    translateMultipleWithAI,
    deleteTranslationKeys,
    deleteNonMasterKeys,
    searchInTranslation,
  } = useTranslations("de"); // Using German as master language

  // Create a wrapper function that can handle both single key and multiple keys
  const handleTranslateWithAI = (lang: string, key: string | string[]) => {
    if (Array.isArray(key)) {
      return translateMultipleWithAI(lang, key);
    } else {
      return translateWithAI(lang, key);
    }
  };

  const [view, setView] = useState<ViewType>("key");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<string[] | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [filters, setFilters] = useState<TranslationFilters>({
    showMissing: false,
    showEmpty: false,
    showIdentical: false,
    showNonMaster: false,
  });

  // Calculate issues for all languages
  const allLanguageIssues = useMemo(() => {
    const issues: Record<string, any> = {};

    if (
      languages.length === 0 ||
      !masterTranslation ||
      Object.keys(translations).length === 0
    ) {
      return issues;
    }

    languages.forEach((lang) => {
      issues[lang] = getLanguageIssues(lang);
    });

    return issues;
  }, [languages, getLanguageIssues, masterTranslation, translations]);

  // Handle search
  const handleSearch = (term: string) => {
    setSearchTerm(term);

    if (!term) {
      setSearchResults(null);
      return;
    }

    if (selectedLang && translations[selectedLang]) {
      const results = searchInTranslation(selectedLang, term);
      setSearchResults(results);
    }
  };

  // Handle selecting a key from issues
  const handleSelectKey = (key: string) => {
    setSelectedKey(key);

    // If we're in JSON view, switch to key view
    if (view === "json") {
      setView("key");
    }

    // Reset filters to show all keys
    setFilters({
      showMissing: false,
      showEmpty: false,
      showIdentical: false,
      showNonMaster: false,
    });

    // Clear search and set results to show just this key
    setSearchTerm("");
    setSearchResults([key]);
  };

  // Reset selection when changing languages
  useEffect(() => {
    setSelectedKey(null);
    if (searchTerm) {
      handleSearch(searchTerm);
    }
  }, [selectedLang]);

  // Main loading state
  if (isLoading && (!languages.length || !Object.keys(translations).length)) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading translations...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-red-400"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm leading-5 text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // If we're switching languages, we might have a brief loading state for just that language
  const isLoadingSelectedLanguage = selectedLang && !translations[selectedLang];

  return (
    <>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">
          Translation Manager
        </h1>
        <p className="mt-2 text-gray-600">
          Manage translations across multiple languages. Master language:{" "}
          <span className="font-semibold">{masterLang.toUpperCase()}</span>
        </p>
      </div>

      <LanguageSelector
        languages={languages}
        selectedLang={selectedLang}
        masterLang={masterLang}
        onSelect={setSelectedLang}
        issues={allLanguageIssues}
      />

      {selectedLang && allLanguageIssues[selectedLang] && (
        <IssuesPanel
          lang={selectedLang}
          issues={
            allLanguageIssues[selectedLang] || {
              missingKeys: [],
              emptyValues: [],
              nonMasterKeys: [],
            }
          }
          onAddMissing={addMissingKeysToLang}
          onDeleteNonMaster={deleteNonMasterKeys}
          onSelectKey={handleSelectKey}
        />
      )}

      <div className="flex gap-2 items-center mb-4">
        <div className="flex-grow">
          <SearchBar onSearch={handleSearch} />
        </div>
        {searchResults && (
          <button
            onClick={() => {
              setSearchTerm("");
              setSearchResults(null);
            }}
            className="px-3 py-1 rounded-md text-sm bg-gray-200 text-gray-700 border border-gray-300 hover:bg-gray-300"
          >
            Clear Search
          </button>
        )}
      </div>

      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
        <ViewToggle view={view} setView={setView} />
        {view === "key" && (
          <FilterBar filters={filters} setFilters={setFilters} />
        )}
      </div>

      {isLoadingSelectedLanguage ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="animate-pulse text-gray-500">
            Loading translations for {selectedLang.toUpperCase()}...
          </div>
        </div>
      ) : (
        selectedLang &&
        translations[selectedLang] && (
          <>
            {view === "key" ? (
              <TranslationEditor
                translation={translations[selectedLang]}
                masterTranslation={masterTranslation}
                lang={selectedLang}
                masterLang={masterLang}
                onUpdate={updateTranslation}
                onCopyFromMaster={copyKeyFromMaster}
                onTranslateWithAI={handleTranslateWithAI}
                onDeleteKeys={deleteTranslationKeys}
                searchResults={searchResults}
                filters={filters}
              />
            ) : (
              <JsonViewer
                data={translations[selectedLang]}
                onSave={(updatedData: TranslationObject) =>
                  updateTranslation(selectedLang, "", updatedData)
                }
              />
            )}
          </>
        )
      )}
    </>
  );
};

export default TranslationOffice;
